import _ from "lodash";

/**
 * Opt-in for classic-game layout overrides: field must exist on the version's gameConfig.
 * Base documents are not consulted.
 */
export function hasLocalizationOverrides(gameConfig) {
  return _.has(gameConfig, "localizationOverrides");
}

/**
 * Prefer Localization-tab language (selectedLanguage) when provided; else options.language.
 */
export function resolveLocalizationOverrideLanguage(gameConfig, preferredLanguage) {
  if (!_.isNil(preferredLanguage) && preferredLanguage !== "") {
    return preferredLanguage;
  }
  return _.get(gameConfig, "options.language", "en");
}

/**
 * True when Version Settings writes should go to localizationOverrides instead of components.
 */
export function shouldUseLocalizationOverride(gameConfig, preferredLanguage) {
  if (!hasLocalizationOverrides(gameConfig)) {
    return false;
  }
  const language = resolveLocalizationOverrideLanguage(gameConfig, preferredLanguage);
  return !_.isNil(language) && language !== "en";
}

/**
 * Matches gameConfig.components.<componentId>.<prop...>
 * Does not match gameConfig.options.* or other top-level paths.
 */
const COMPONENT_SETTING_PATH_REGEX = /^gameConfig\.components\.([^.]+)(\..+)$/;

/**
 * Asset-slot maps: nested keys (e.g. selectedImages.button) are stored sparsely
 * so sibling slots stay on English unless explicitly overridden.
 * Other nested props (e.g. position.portrait.y) still expand to the full top-level object.
 */
const PARTIAL_SLOT_MAP_PROPS = new Set(["selectedImages", "selectedAtlases", "selectedSounds"]);

export function isPartialSlotMapProp(propertyId) {
  return PARTIAL_SLOT_MAP_PROPS.has(propertyId);
}

/**
 * English base + sparse override slots (override wins per key). Arrays are replaced per slot, not deep-merged.
 */
export function mergePartialSlotMap(englishValue, overrideValue) {
  return {
    ...(_.isPlainObject(englishValue) ? _.cloneDeep(englishValue) : {}),
    ...(_.isPlainObject(overrideValue) ? _.cloneDeep(overrideValue) : {})
  };
}

/**
 * When override mode is active, rewrite a Version Settings write path from
 * gameConfig.components.<id>.<prop...> to gameConfig.localizationOverrides.<lang>.<id>.<topLevelProp>.
 *
 * Nested writes (e.g. position.portrait.y) always persist the full top-level property
 * (e.g. entire position including portrait + landscape), never a sparse leaf path.
 *
 * Exception: selectedImages / selectedAtlases / selectedSounds store only the edited
 * slot key(s), so siblings remain English until overridden separately.
 *
 * English mode leaves component paths unchanged and never writes localizationOverrides.
 * Non-English mode never writes gameConfig.components.* for matched setting paths.
 *
 * @param {{ path: string, value: *, [key: string]: * }} payload
 * @param {Object} gameConfig - real (non-effective) version gameConfig
 * @param {string} [preferredLanguage] - Localization-tab language when available
 */
export function rewriteComponentPathForLocalizationOverride(payload, gameConfig, preferredLanguage) {
  if (_.isNil(payload) || _.isNil(payload.path) || !hasLocalizationOverrides(gameConfig)) {
    return payload;
  }

  const language = resolveLocalizationOverrideLanguage(gameConfig, preferredLanguage);
  const isOverrideMode = !_.isNil(language) && language !== "en";
  const isComponentSettingPath = COMPONENT_SETTING_PATH_REGEX.test(payload.path);

  // English: only version settings (components). Never redirect into overrides.
  if (!isOverrideMode) {
    return payload;
  }

  // Non-English: component setting edits go only to localizationOverrides[lang].
  if (!isComponentSettingPath) {
    return payload;
  }

  const match = COMPONENT_SETTING_PATH_REGEX.exec(payload.path);
  const componentId = match[1];
  const propertyPath = match[2].replace(/^\./, ""); // e.g. "position.portrait.y" or "textPosition"
  const pathParts = propertyPath.split(".");
  const topLevelProp = pathParts[0];
  const nestedPath = pathParts.slice(1).join(".");
  const overrideRootPath = `gameConfig.localizationOverrides.${language}.${componentId}.${topLevelProp}`;

  // Caption/font localization lives under components.*.localization — never redirect those.
  if (topLevelProp === "localization") {
    return payload;
  }

  // Already writing the whole top-level setting (e.g. textPosition object)
  if (!nestedPath) {
    return {
      ...payload,
      path: overrideRootPath
    };
  }

  const existingOverride = _.get(
    gameConfig,
    `localizationOverrides.${language}.${componentId}.${topLevelProp}`
  );

  // Asset slot maps: keep override sparse (only edited keys), never bake English siblings in.
  if (isPartialSlotMapProp(topLevelProp)) {
    const sparseValue = _.isPlainObject(existingOverride) ? _.cloneDeep(existingOverride) : {};
    const slotKey = pathParts[1];
    const restWithinSlot = pathParts.slice(2).join(".");

    if (!restWithinSlot) {
      sparseValue[slotKey] = _.cloneDeep(payload.value);
    } else {
      const englishSlot = _.get(gameConfig, `components.${componentId}.${topLevelProp}.${slotKey}`);
      const existingSlot = _.get(sparseValue, slotKey);
      const slotValue = _.cloneDeep(!_.isNil(existingSlot) ? existingSlot : englishSlot);
      _.set(slotValue, restWithinSlot, payload.value);
      sparseValue[slotKey] = slotValue;
    }

    return {
      ...payload,
      path: overrideRootPath,
      value: sparseValue
    };
  }

  // Expand leaf writes into a complete top-level object from English base + existing override.
  // Read English only from components — never mutate components here.
  const englishBase = _.get(gameConfig, `components.${componentId}.${topLevelProp}`);
  const completeValue = _.merge(
    {},
    _.isPlainObject(englishBase) ? _.cloneDeep(englishBase) : {},
    _.isPlainObject(existingOverride) ? _.cloneDeep(existingOverride) : {}
  );
  _.set(completeValue, nestedPath, payload.value);

  return {
    ...payload,
    path: overrideRootPath,
    value: completeValue
  };
}

/**
 * Display-only clone: merges localizationOverrides[language] onto components.
 * Does not mutate the stored English base on the original gameConfig.
 *
 * @param {Object} gameConfig
 * @param {string} [preferredLanguage]
 */
export function buildEffectiveGameConfig(gameConfig, preferredLanguage) {
  const cloned = _.cloneDeep(gameConfig);
  if (!shouldUseLocalizationOverride(gameConfig, preferredLanguage)) {
    return cloned;
  }

  const language = resolveLocalizationOverrideLanguage(gameConfig, preferredLanguage);
  const languageOverrides = _.get(gameConfig, `localizationOverrides.${language}`, {});

  Object.entries(languageOverrides).forEach(([componentId, overrideProps]) => {
    if (_.isNil(overrideProps)) {
      return;
    }
    Object.entries(overrideProps).forEach(([propertyId, propertyDetails]) => {
      if (isPartialSlotMapProp(propertyId) && _.isPlainObject(propertyDetails)) {
        const englishValue = _.get(cloned, `components.${componentId}.${propertyId}`, {});
        _.set(
          cloned,
          `components.${componentId}.${propertyId}`,
          mergePartialSlotMap(englishValue, propertyDetails)
        );
        return;
      }
      _.set(cloned, `components.${componentId}.${propertyId}`, _.cloneDeep(propertyDetails));
    });
  });

  return cloned;
}

/**
 * Removes one language's layout overrides from a gameConfig (mutates and returns it).
 */
export function stripLanguageFromLocalizationOverrides(gameConfig, languageKey) {
  if (_.isNil(gameConfig) || _.isNil(languageKey)) {
    return gameConfig;
  }
  _.unset(gameConfig, `localizationOverrides.${languageKey}`);
  return gameConfig;
}

/**
 * When a version already has localizationOverrides, ensure the given language
 * has an entry (empty object if new). Does nothing if the field is absent or language is "en".
 * Mutates and returns gameConfig.
 */
export function ensureLocalizationOverrideLanguage(gameConfig, languageKey) {
  if (!hasLocalizationOverrides(gameConfig) || _.isNil(languageKey) || languageKey === "en") {
    return gameConfig;
  }
  if (!_.has(gameConfig, `localizationOverrides.${languageKey}`)) {
    _.set(gameConfig, `localizationOverrides.${languageKey}`, {});
  }
  return gameConfig;
}
